import { SALARY_CALCULATION, GRADES_IDE } from '../constants/salary';
import { calculateProgressiveTax } from './taxCalculations';
import { PrimesData, OverseasTerritoryId, CotisationSalariale, CalculatedCotisation } from '../types';
import { calculateTotalMonthlyPrimes } from './primeCalculations';
import { TEMPORARY_COMPLEMENT_MAJORATION, DOM_TOM_TAX_ABATEMENTS } from '../constants/business';
import { findOverseasTerritoryById } from '../types';
import { DEFAULT_COTISATIONS } from '../services/cotisationService';

/**
 * @fileoverview Salary calculation utilities for French public hospital system
 * 
 * This module provides comprehensive salary calculation functions for healthcare professionals
 * in the French public hospital system (Fonction Publique Hospitalière). It handles:
 * - Gross salary calculations based on indice majoré
 * - Net salary after social contributions
 * - Tax calculations with progressive rates
 * - Prime integration and work time adjustments
 * 
 * All calculations follow official French government regulations and tax brackets for 2024.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

/**
 * Complete salary calculation result with all components
 * 
 * @interface SalaryCalculationResult
 * @property {number} grossAnnual - Gross annual salary before any deductions
 * @property {number} grossMonthly - Gross monthly salary before any deductions
 * @property {number} withPrimesAnnual - Annual salary including all active primes
 * @property {number} withPrimesMonthly - Monthly salary including all active primes
 * @property {number} netAfterChargesAnnual - Annual net salary after social contributions
 * @property {number} netAfterChargesMonthly - Monthly net salary after social contributions
 * @property {number} netAfterTaxAnnual - Annual net salary after taxes
 * @property {number} netAfterTaxMonthly - Monthly net salary after taxes
 * @property {number} primesAmount - Total annual amount of all active primes
 * @property {number} abattementAmount - Annual tax allowance for professional expenses
 * @property {number} taxableIncome - Annual taxable income after allowances
 * @property {number} domTomTaxAbatement - Annual DOM-TOM tax abatement amount
 */
export interface SalaryCalculationResult {
  grossAnnual: number;
  grossMonthly: number;
  withPrimesAnnual: number;
  withPrimesMonthly: number;
  netAfterChargesAnnual: number;
  netAfterChargesMonthly: number;
  netAfterTaxAnnual: number;
  netAfterTaxMonthly: number;
  primesAmount: number;
  abattementAmount: number;
  taxableIncome: number;
  domTomTaxAbatement: number;
  calculatedCotisations: CalculatedCotisation[];
}

/**
 * Calculates complete salary breakdown for a public hospital employee
 * 
 * This is the main salary calculation function that computes all salary components
 * from gross salary to final net amount after taxes. It integrates:
 * - Base salary from grade and echelon
 * - Work time percentage adjustments
 * - All active primes and allowances
 * - Social contributions (22% rate)
 * - Progressive income tax calculation
 * - Professional expense allowances
 * 
 * @param {number|null} selectedGrade - The employee's grade (1 or 2 for nurses)
 * @param {number} selectedEchelon - The employee's echelon within the grade (1-11)
 * @param {number} workTimePercentage - Work time as percentage of full-time (0-100)
 * @param {PrimesData} [primesData] - Optional primes data with active primes
 * @param {OverseasTerritoryId} [selectedOverseasTerritory] - Optional overseas territory for majoration
 * @returns {SalaryCalculationResult|null} Complete salary calculation or null if invalid inputs
 * 
 * @example
 * // Calculate salary for Grade 1, Echelon 6, full-time with primes
 * const salary = calculateSalary(1, 6, 100, primesData);
 * console.log(salary.grossMonthly); // 2,550€
 * console.log(salary.netAfterTaxMonthly); // 2,100€
 * 
 * @example
 * // Calculate salary for part-time (80%) without primes
 * const salary = calculateSalary(2, 8, 80);
 * console.log(salary.grossMonthly); // 2,435€ (80% of full-time)
 * console.log(salary.netAfterTaxMonthly); // 2,010€
 * 
 * @see {@link GRADES_IDE} For available grades and echelons
 * @see {@link calculateProgressiveTax} For tax calculation details
 */
export function calculateSalary(
  selectedGrade: number | null,
  selectedEchelon: number | null,
  workTimePercentage: number,
  grades: GradeData[],
  primesData?: PrimesData,
  selectedOverseasTerritory: OverseasTerritoryId = null,
  cotisationsData?: { cotisations: CotisationSalariale[] }
): SalaryCalculationResult | null {
  console.log('💰 calculateSalary - Called with parameters:', {
    selectedGrade,
    selectedEchelon,
    workTimePercentage,
    gradesLength: grades.length,
    selectedOverseasTerritory,
    primesDataLength: primesData?.primes?.length || 0,
    gradesData: grades.map(g => ({
      id: g.id,
      nom: g.nom,
      echelonsCount: g.echelons.length,
      echelons: g.echelons.map(e => ({ echelon: e.echelon, indiceMajoré: e.indiceMajoré }))
    }))
  });

  if (!selectedGrade || selectedEchelon === null) {
    console.log('💰 calculateSalary - Invalid inputs, returning null');
    return null;
  }

  console.log('💰 calculateSalary - Looking for grade', selectedGrade, 'in grades array with', grades.length, 'grades');
  console.log('💰 calculateSalary - Available grade IDs:', grades.map(g => g.id));
  
  const targetGrade = grades.find(grade => grade.id === selectedGrade);
  console.log('💰 calculateSalary - Found targetGrade:', targetGrade ? {
    id: targetGrade.id,
    nom: targetGrade.nom,
    echelonsCount: targetGrade.echelons.length,
    availableEchelons: targetGrade.echelons.map(e => e.echelon)
  } : 'null');
  
  const currentEchelon = grades
    .find(grade => grade.id === selectedGrade)
    ?.echelons.find(e => e.echelon === selectedEchelon);

  console.log('💰 calculateSalary - Looking for echelon', selectedEchelon, 'in grade', selectedGrade);
  console.log('💰 calculateSalary - Found currentEchelon:', currentEchelon ? {
    echelon: currentEchelon.echelon,
    indiceMajoré: currentEchelon.indiceMajoré,
    echelonLabel: currentEchelon.echelonLabel,
    echelonRangeLabel: currentEchelon.echelonRangeLabel
  } : 'null');

  if (!currentEchelon) {
    console.log('💰 calculateSalary - No echelon found for grade', selectedGrade, 'echelon', selectedEchelon, ', returning null');
    console.log('💰 calculateSalary - Available echelons for this grade:', targetGrade?.echelons.map(e => e.echelon) || 'no grade found');
    return null;
  }

  console.log('💰 calculateSalary - Using indiceMajoré:', currentEchelon.indiceMajoré);

  // Calculate gross salary
  const grossAnnual = calculateGrossSalary(
    currentEchelon.indiceMajoré, 
    workTimePercentage,
    selectedOverseasTerritory
  );
  console.log('💰 calculateSalary - Calculated grossAnnual:', grossAnnual);
  
  const grossMonthly = grossAnnual / SALARY_CALCULATION.MONTHS_PER_YEAR;
  console.log('💰 calculateSalary - Calculated grossMonthly:', grossMonthly);

  // Calculate primes
  const primesTotalMonthly = primesData 
    ? calculateTotalMonthlyPrimes(primesData.primes, workTimePercentage)
    : 0;
  const primesTotalAnnual = primesTotalMonthly * SALARY_CALCULATION.MONTHS_PER_YEAR;

  // Calculate salary with primes
  const withPrimesAnnual = grossAnnual + primesTotalAnnual;
  const withPrimesMonthly = withPrimesAnnual / SALARY_CALCULATION.MONTHS_PER_YEAR;

  // Calculate individual cotisations - always use either Supabase data or fallback
  const cotisationsToUse = (cotisationsData?.cotisations && cotisationsData.cotisations.length > 0) 
    ? cotisationsData.cotisations 
    : DEFAULT_COTISATIONS;
  
  const calculatedCotisations = calculateIndividualCotisations(
    grossMonthly, 
    withPrimesMonthly, 
    cotisationsToUse, 
    primesData?.primes || []
  );
  
  // Calculate net after social contributions
  const totalCotisationsAnnual = calculatedCotisations.reduce((total, cotisation) => total + cotisation.montantAnnuel, 0);
  const netAfterChargesAnnual = withPrimesAnnual - totalCotisationsAnnual;
  const netAfterChargesMonthly = netAfterChargesAnnual / SALARY_CALCULATION.MONTHS_PER_YEAR;

  // Calculate tax allowance and taxable income
  const abattementAmount = calculateTaxAllowance(netAfterChargesAnnual);
  
  // Calculate DOM-TOM tax abatement if applicable
  const domTomTaxAbatement = selectedOverseasTerritory ? calculateDomTomTaxAbatement(netAfterChargesAnnual - abattementAmount, selectedOverseasTerritory) : 0;
  
  const taxableIncome = netAfterChargesAnnual - abattementAmount - domTomTaxAbatement;

  // Calculate taxes
  const taxCalculation = calculateProgressiveTax(taxableIncome);
  const netAfterTaxAnnual = netAfterChargesAnnual - taxCalculation.tax;
  const netAfterTaxMonthly = netAfterTaxAnnual / SALARY_CALCULATION.MONTHS_PER_YEAR;

  return {
    grossAnnual,
    grossMonthly,
    withPrimesAnnual,
    withPrimesMonthly,
    netAfterChargesAnnual,
    netAfterChargesMonthly,
    netAfterTaxAnnual,
    netAfterTaxMonthly,
    primesAmount: primesTotalAnnual,
    abattementAmount,
    taxableIncome,
    domTomTaxAbatement,
    calculatedCotisations
  };
}

/**
 * Calculates gross annual salary from indice majoré and work time percentage
 * 
 * The gross salary calculation uses the official point value system:
 * - Each indice majoré point has a fixed euro value (4.92278€ in 2024)
 * - Annual salary = indice majoré × point value × 12 months
 * - Work time percentage is applied for part-time employees
 * 
 * @param {number} indiceMajoré - The salary index from the official grid
 * @param {number} workTimePercentage - Work time as percentage of full-time (0-100)
 * @param {OverseasTerritoryId} selectedOverseasTerritory - Overseas territory for majoration
 * @returns {number} The gross annual salary in euros
 * 
 * @example
 * // Calculate full-time gross salary for IM 472
 * const grossSalary = calculateGrossSalary(472, 100);
 * console.log(grossSalary); // 27,882€ (472 × 4.92278€ × 12)
 * 
 * @example
 * // Calculate part-time (80%) gross salary for IM 472
 * const grossSalary = calculateGrossSalary(472, 80);
 * console.log(grossSalary); // 22,306€ (27,882€ × 80%)
 * 
 * @private
 */
function calculateGrossSalary(indiceMajoré: number, workTimePercentage: number, selectedOverseasTerritory: OverseasTerritoryId = null): number {
  console.log('💰 calculateGrossSalary - Called with:', {
    indiceMajoré,
    workTimePercentage,
    selectedOverseasTerritory
  });

  let baseSalary = (indiceMajoré * SALARY_CALCULATION.POINT_VALUE * SALARY_CALCULATION.MONTHS_PER_YEAR) 
    * (workTimePercentage / SALARY_CALCULATION.FULL_TIME_PERCENTAGE);
  
  console.log('💰 calculateGrossSalary - Base salary before overseas majoration:', baseSalary);

  // Apply overseas territory majoration if applicable
  if (selectedOverseasTerritory) {
    const territory = findOverseasTerritoryById(selectedOverseasTerritory);
    if (territory) {
      console.log('💰 calculateGrossSalary - Applying overseas majoration:', territory.majoration + '%');
      baseSalary = baseSalary * (1 + territory.majoration / 100);
      console.log('💰 calculateGrossSalary - Salary after overseas majoration:', baseSalary);
    }
    
    // Apply temporary complement if applicable
    if (territory?.complement) {
      console.log('💰 calculateGrossSalary - Applying temporary complement:', territory.complement + '%');
      baseSalary = baseSalary * (1 + territory.complement / 100);
      console.log('💰 calculateGrossSalary - Final salary after temporary complement:', baseSalary);
    }
  }
  
  console.log('💰 calculateGrossSalary - Final calculated salary:', baseSalary);
  return baseSalary;
}

/**
 * Calculates the tax allowance for professional expenses (abattement forfaitaire)
 * 
 * The French tax system automatically applies a 10% allowance on salary income
 * to account for professional expenses. This allowance has minimum and maximum limits:
 * - Rate: 10% of net salary
 * - Minimum: 429€ per year
 * - Maximum: 12,652€ per year
 * 
 * This allowance reduces the taxable income base for income tax calculation.
 * 
 * @param {number} netSalary - The net annual salary after social contributions
 * @returns {number} The annual tax allowance amount in euros
 * 
 * @example
 * // Calculate tax allowance for 25,000€ net salary
 * const allowance = calculateTaxAllowance(25000);
 * console.log(allowance); // 2,500€ (10% of 25,000€)
 * 
 * @example
 * // Calculate tax allowance for very low salary (minimum applies)
 * const allowance = calculateTaxAllowance(3000);
 * console.log(allowance); // 429€ (minimum allowance)
 * 
 * @example
 * // Calculate tax allowance for very high salary (maximum applies)
 * const allowance = calculateTaxAllowance(200000);
 * console.log(allowance); // 12,652€ (maximum allowance)
 * 
 * @see {@link https://www.impots.gouv.fr/particulier/questions/comment-sont-calculees-les-charges-deductibles-du-revenu} Official tax allowance rules
 * @private
 */
function calculateTaxAllowance(netSalary: number): number {
  const allowanceAmount = netSalary * SALARY_CALCULATION.TAX_ALLOWANCE_RATE;
  return Math.min(
    SALARY_CALCULATION.TAX_ALLOWANCE_MAX, 
    Math.max(SALARY_CALCULATION.TAX_ALLOWANCE_MIN, allowanceAmount)
  );
}

/**
 * Calculates the DOM-TOM tax abatement for overseas territories
 * 
 * Residents of certain overseas territories benefit from a tax abatement on their income tax:
 * - 30% for Guadeloupe, Martinique, and La Réunion (capped at 2,450€)
 * - 40% for Guyane and Mayotte (capped at 4,050€)
 * 
 * This abatement is applied after the standard 10% professional expense allowance.
 * 
 * @param {number} taxableIncomeAfterStandardAbatement - Taxable income after 10% allowance
 * @param {OverseasTerritoryId} selectedOverseasTerritory - Selected overseas territory
 * @returns {number} The DOM-TOM tax abatement amount in euros
 * 
 * @example
 * // Calculate DOM-TOM abatement for Guadeloupe with 25,000€ taxable income
 * const abatement = calculateDomTomTaxAbatement(25000, 'guadeloupe');
 * console.log(abatement); // 2,450€ (30% of 25,000€ capped at 2,450€)
 * 
 * @private
 */
function calculateDomTomTaxAbatement(
  taxableIncomeAfterStandardAbatement: number, 
  selectedOverseasTerritory: OverseasTerritoryId
): number {
  if (!selectedOverseasTerritory) {
    return 0;
  }
  
  const territory = findOverseasTerritoryById(selectedOverseasTerritory);
  if (!territory?.abatementRate || !territory?.abatementCap) {
    return 0;
  }
  
  const abatementAmount = taxableIncomeAfterStandardAbatement * (territory.abatementRate / 100);
  return Math.min(abatementAmount, territory.abatementCap);
}

/**
 * Calculates individual social contributions based on Supabase cotisations_salariales data
 * 
 * This function calculates each social contribution individually using the rates and rules
 * defined in the cotisations_salariales table. It handles different rates for low/high income
 * and applies TIBM ceilings where applicable.
 * 
 * @param {number} grossMonthly - Gross monthly salary (traitement indiciaire only)
 * @param {number} grossMonthlyWithPrimes - Gross monthly salary including all primes
 * @param {CotisationSalariale[]} cotisations - Array of cotisation definitions from Supabase
 * @param {PrimeIndemnite[]} primes - Array of all primes for specific assiette calculations
 * @returns {CalculatedCotisation[]} Array of calculated cotisations with amounts and details
 * 
 * @example
 * // Calculate cotisations for 2,500€ gross monthly with primes
 * const cotisations = calculateIndividualCotisations(2200, 2500, cotisationsData, primes);
 * console.log(cotisations); // [{ nom: 'CNRACL', montantMensuel: 268.75, ... }, ...]
 */
function calculateIndividualCotisations(
  grossMonthly: number,
  grossMonthlyWithPrimes: number,
  cotisations: CotisationSalariale[],
  primes: PrimeIndemnite[]
): CalculatedCotisation[] {
  const grossAnnualWithPrimes = grossMonthlyWithPrimes * 12;
  const isHighIncome = grossAnnualWithPrimes >= 15700; // Seuil de 15,700€ annuel
  
  return cotisations.map(cotisation => {
    // Determine which rate to use based on cotisation type and income level
    let tauxApplique: number;
    let tauxExplication: string;
    
    if (cotisation.id.toLowerCase().includes('cnracl')) {
      // CNRACL uses the fixed 'rate' column, not income-dependent rates
      tauxApplique = cotisation.taux;
      tauxExplication = 'taux fixe CNRACL';
    } else {
      // Other cotisations use income-dependent rates
      tauxApplique = isHighIncome ? cotisation.tauxHaut : cotisation.tauxBas;
      tauxExplication = isHighIncome ? 'taux majoré (revenus ≥ 15,700€)' : 'taux réduit (revenus < 15,700€)';
    }
    
    // Calculate base amount (assiette) - specific calculation for CNRACL
    let assietteMontant = grossMonthlyWithPrimes;
    let assietteDetail = 'Salaire brut mensuel avec toutes les primes';
    
    // CNRACL has specific assiette: Traitement indiciaire + CTI + NBI
    // L'assiette CNRACL doit toujours inclure le CTI et la NBI du titulaire/stagiaire,
    // indépendamment de l'état "active" du toggle UI (ce sont des éléments indiciaires).
    if (cotisation.id.toLowerCase().includes('cnracl')) {
      const ctiPrime = primes.find(p => p.id === 'segur');
      const nbiPrime = primes.find(p => p.id === 'nbi');

      const ctiAmount = ctiPrime ? ctiPrime.montantUtilisateur : 0;
      const nbiAmount = nbiPrime && nbiPrime.active ? nbiPrime.montantUtilisateur : 0;
      
      assietteMontant = grossMonthly + ctiAmount + nbiAmount;
      assietteDetail = `Traitement indiciaire (${grossMonthly.toFixed(2)}€) + Prime CTI (${ctiAmount.toFixed(2)}€) + NBI (${nbiAmount.toFixed(2)}€)`;
    }
    
    // RAFP has specific assiette: Sum of all active primes (IR + SFT + Primes + Avantages en nature)
    if (cotisation.id.toLowerCase().includes('rafp')) {
      // Calculate sum of all active primes
      const activePrimes = primes.filter(p => p.active);
      const sumOfPrimes = activePrimes.reduce((sum, prime) => sum + prime.montantUtilisateur, 0);
      
      // Apply RAFP ceiling (20% of gross monthly salary)
      const plafondRAFP = grossMonthly * cotisation.plafondTIBM; // plafondTIBM should be 0.2 for RAFP
      assietteMontant = Math.min(sumOfPrimes, plafondRAFP);
      
      // Generate detailed explanation
      const primesDetails = activePrimes.length > 0 
        ? activePrimes.map(p => `${p.nom} (${p.montantUtilisateur.toFixed(2)}€)`).join(' + ')
        : 'Aucune prime active';
      
      assietteDetail = `Somme des primes actives: ${primesDetails} = ${sumOfPrimes.toFixed(2)}€, plafonnée à ${(cotisation.plafondTIBM * 100).toFixed(0)}% du traitement (${plafondRAFP.toFixed(2)}€)`;
    }
    
    // Apply TIBM ceiling if applicable (plafondTIBM < 1 means there's a ceiling)
    if (cotisation.plafondTIBM < 1 && !cotisation.id.toLowerCase().includes('rafp')) {
      // Calculate TIBM ceiling: gross monthly × plafondTIBM
      const plafondMontant = grossMonthlyWithPrimes * cotisation.plafondTIBM;
      assietteMontant = Math.min(assietteMontant, plafondMontant);
    }

    // Apply 1.75% abatement for CSG and CRDS (assiette = 98.25% du revenu)
    const isCsgOrCrds = cotisation.id.toLowerCase().includes('csg') || cotisation.id.toLowerCase().includes('crds');
    if (isCsgOrCrds) {
      assietteMontant = assietteMontant * SALARY_CALCULATION.CSG_CRDS_ABATTEMENT;
      assietteDetail += ` — Abattement 1,75% appliqué (assiette = 98,25%)`;
    }
    
    // Calculate monthly cotisation amount
    const montantMensuel = assietteMontant * tauxApplique;
    const montantAnnuel = montantMensuel * 12;
    
    // Generate detailed calculation explanation for tooltip
    const tauxPourcentage = tauxApplique * 100;
    let calculDetail = `Base de calcul: ${cotisation.assiette}\n\n`;
    calculDetail += `Détail de l'assiette:\n${assietteDetail}\n`;
    calculDetail += `Montant de l'assiette: ${assietteMontant.toFixed(2)}€`;
    
    if (cotisation.plafondTIBM < 1) {
      calculDetail += ` (plafonné à ${(cotisation.plafondTIBM * 100).toFixed(0)}% de l'assiette)`;
    }
    
    calculDetail += `\n\nTaux appliqué: ${tauxPourcentage.toFixed(2)}% (${tauxExplication})`;
    
    calculDetail += `\n\nCalcul final:\n${assietteMontant.toFixed(2)}€ × ${tauxPourcentage.toFixed(2)}% = ${montantMensuel.toFixed(2)}€`;
    
    return {
      nom: cotisation.nom,
      montantMensuel: Math.round(montantMensuel * 100) / 100,
      montantAnnuel: Math.round(montantAnnuel * 100) / 100,
      detail: cotisation.detail,
      calculDetail,
      tauxApplique: tauxPourcentage,
      assietteMontant: Math.round(assietteMontant * 100) / 100
    };
  });
}